import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { goAfterLogin, postLoginTarget } from "@/features/auth/postLogin";
import { ApiError } from "@/lib/api";
import { useVerify2fa } from "@/features/auth/hooks";
import { startLoginIntro } from "@/features/auth/intro";
import { AuthLayout } from "./AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/field";
import { cn } from "@/lib/utils";

export function TwoFactorPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const verify = useVerify2fa();
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const [backup, setBackup] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  async function submitCode(code: string) {
    setError(null);
    try {
      await verify.mutateAsync({ code });
      // Kirish muvaffaqiyatli: FATH logo animatsiyasi, platforma uning ostida ochiladi
      startLoginIntro();
      goAfterLogin(postLoginTarget(location), navigate);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("twofa.failed"));
      if (err instanceof ApiError && err.code === "session_expired") navigate("/login", { replace: true });
      setDigits(Array(6).fill(""));
      refs.current[0]?.focus();
    }
  }

  function onDigit(i: number, value: string) {
    const clean = value.replace(/\D/g, "");
    if (!clean) {
      setDigits((d) => d.map((x, j) => (j === i ? "" : x)));
      return;
    }
    const next = [...digits];
    clean.split("").slice(0, 6 - i).forEach((ch, k) => (next[i + k] = ch));
    setDigits(next);
    const last = Math.min(5, i + clean.length);
    refs.current[last]?.focus();
    if (next.every((d) => d !== "")) submitCode(next.join(""));
  }

  function onKey(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  }

  return (
    <AuthLayout title={t("twofa.title")} subtitle={backup ? t("twofa.subtitleBackup") : t("twofa.subtitle")}>
      {error && (
        <div role="alert" className="mb-4 rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px] text-danger">
          {error}
        </div>
      )}
      {backup ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitCode(backupCode.trim());
          }}
          className="space-y-4"
        >
          <Input autoFocus value={backupCode} onChange={(e) => setBackupCode(e.target.value)} placeholder="xxxx-xxxx" className="text-center font-mono" />
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={verify.isPending}>
            {t("twofa.verify")}
          </Button>
        </form>
      ) : (
        <>
          <div className="mb-5 flex justify-center gap-2" onPaste={(e) => { e.preventDefault(); onDigit(0, e.clipboardData.getData("text")); }}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => { refs.current[i] = el; }}
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                autoFocus={i === 0}
                value={d}
                onChange={(e) => onDigit(i, e.target.value)}
                onKeyDown={(e) => onKey(i, e)}
                className={cn("h-12 w-10 rounded-[10px] border border-border text-center text-[20px] font-bold focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20")}
                aria-label={t("twofa.digit", { n: i + 1 })}
              />
            ))}
          </div>
          <Button variant="primary" size="lg" className="w-full" loading={verify.isPending} onClick={() => submitCode(digits.join(""))} disabled={digits.some((d) => !d)}>
            {t("twofa.verify")}
          </Button>
        </>
      )}
      <div className="mt-4 flex justify-between text-[12px]">
        <button type="button" onClick={() => setBackup((b) => !b)} className="text-primary hover:underline">
          {backup ? t("twofa.useAuthenticator") : t("twofa.useBackup")}
        </button>
        <Link to="/login" className="text-primary hover:underline">
          {t("twofa.backToSignIn")}
        </Link>
      </div>
      <p className="mt-5 text-center text-[11px] text-muted">{t("twofa.lostPhone")}</p>
    </AuthLayout>
  );
}
